import { memo, useMemo, useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { cn } from '@/shared/utils/cn';
import {
  ProgressBarProps,
  ProgressBarColor,
  ProgressBarSize,
  PROGRESS_COLOR_MAP,
} from '@/shared/types/progressbar';
import { Colors } from '@/shared/types/types';
import { BarProgress, HelperText, LabelProgress } from './atoms';
import { Button } from '@/modules/Button';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

/** Tamaños válidos para ProgressBar */
const VALID_SIZES: ProgressBarSize[] = ['xxs', 'xs', 'sm', 'md', 'lg'];

/**
 * Hook interno para manejar la cuenta regresiva del ProgressBar.
 * Anima el progreso desde un valor inicial hasta 0 en un tiempo determinado.
 */
const useCountdown = (
  initialProgress: number,
  countdownSeconds: number | undefined,
  onComplete?: () => void,
) => {
  const [currentProgress, setCurrentProgress] = useState(initialProgress);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef(false);
  // Guardar el callback en una ref para evitar que esté en las dependencias del useEffect
  const onCompleteRef = useRef(onComplete);

  const INTERVAL_MS = 16; // ~60fps

  // Mantener la ref actualizada
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    if (!countdownSeconds || countdownSeconds <= 0) {
      setCurrentProgress(initialProgress);
    }
  }, [initialProgress, countdownSeconds]);

  useEffect(() => {
    if (!countdownSeconds || countdownSeconds <= 0) {
      return;
    }

    // Resetear el flag de completado cuando cambian los parámetros
    hasCompletedRef.current = false;

    // Limpiar intervalo anterior
    cleanup();

    // Establecer valores iniciales
    setCurrentProgress(initialProgress);
    startTimeRef.current = performance.now();

    // Crear intervalo para la animación
    intervalRef.current = setInterval(() => {
      const now = performance.now();
      const elapsedMs = now - startTimeRef.current;
      const elapsedSeconds = elapsedMs / 1000;

      // Calcular ratio de tiempo (0 a 1)
      const timeRatio = elapsedSeconds / countdownSeconds;

      // Si llegamos al final
      if (timeRatio >= 1) {
        setCurrentProgress(0);
        cleanup();

        // Ejecutar callback solo una vez usando la ref
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          onCompleteRef.current?.();
        }
        return;
      }

      // Calcular nuevo progreso: de initialProgress a 0
      const newProgress = initialProgress * (1 - timeRatio);

      // Redondear a 1 decimal
      const roundedProgress = Math.round(newProgress * 10) / 10;

      setCurrentProgress(roundedProgress);
    }, INTERVAL_MS);

    return cleanup;
  }, [initialProgress, countdownSeconds, cleanup]);

  // Limpiar al desmontar
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return currentProgress;
};

/**
 * Hook para normalizar props legacy a las nuevas
 */
const useNormalizedProps = (props: ProgressBarProps) => {
  return useMemo(() => {
    const {
      color = 'brand',
      size = 'xxs',
      progressChar,
      progressSuffix,
      description,
      helperText,
      helperTextType,
      ...rest
    } = props;

    // Normalizar color: si es legacy (Colors), convertir a ProgressBarColor
    const normalizedColor: ProgressBarColor = [
      'brand',
      'success',
      'danger',
      'warning',
      'info',
      'neutral',
    ].includes(color as string)
      ? (color as ProgressBarColor)
      : PROGRESS_COLOR_MAP[color as Colors] || 'brand';

    // Normalizar tamaño: si no es un tamaño válido, usar 'lg'
    const normalizedSize: ProgressBarSize = VALID_SIZES.includes(size as ProgressBarSize)
      ? (size as ProgressBarSize)
      : 'lg';

    // Normalizar suffix
    const normalizedSuffix = progressSuffix || progressChar || '%';

    // Normalizar helper text
    const normalizedHelperText = helperText || description;
    const normalizedHelperType =
      helperTextType || (normalizedColor === 'danger' ? 'error' : 'custom');

    return {
      ...rest,
      color: normalizedColor,
      size: normalizedSize,
      progressSuffix: normalizedSuffix,
      helperText: normalizedHelperText,
      helperTextType: normalizedHelperType,
    };
  }, [props]);
};

/**
 * ProgressBar - Componente de barra de progreso completo
 *
 * Sigue las especificaciones de Figma con dos modos:
 *
 * **Modo Normal (compact=false)**:
 * - Header: Label | spacer | 75% (eta) [x]
 * - Bar: barra de progreso completa
 * - Helper text
 *
 * **Modo Compacto (compact=true)**:
 * - Header en una línea: Label | _bar | 75% (eta) [x]
 * - Helper text debajo
 *
 * @example
 * // Modo normal
 * <ProgressBar progress={75} label="archivo.zip" />
 *
 * // Modo compacto - barra en línea con label
 * <ProgressBar progress={75} label="archivo.zip" compact />
 */
export const ProgressBar = memo(
  ({
    className,
    progress = 0,
    label,
    showLabel = true,
    showProgress = true,
    eta,
    showCloseButton = false,
    onClose,
    duration = 0,
    withBackground = true,
    compact = false,
    disabled = false,
    countdown,
    onCountdownComplete,
    // Legacy props (deprecated)
    icon,
    showIcon,
    borderStyle,
    status,
    ...restProps
  }: ProgressBarProps) => {
    // Si hay countdown, usar el hook para animar el progreso
    const countdownProgress = useCountdown(progress, countdown, onCountdownComplete);

    // Usar el progreso del countdown si está activo, si no usar el progress directo
    const effectiveProgress = countdown && countdown > 0 ? countdownProgress : progress;

    // Si hay countdown, desactivar la transición CSS para que la animación JS sea suave
    const effectiveDuration = countdown && countdown > 0 ? 0 : duration;
    // Normalizar props
    const { color, size, progressSuffix, helperText, helperTextType } = useNormalizedProps({
      ...restProps,
      progress,
      label,
    });

    const handleClose = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onClose?.();
      },
      [onClose],
    );

    // Clases del contenedor principal
    const containerClasses = cn(
      'ui-imj',
      'ui-relative ui-flex ui-w-full ui-flex-col ui-gap-0',
      disabled && 'ui-opacity-50 ui-pointer-events-none',
      className,
    );

    // Renderizar el grupo de progreso (porcentaje + eta + botón cerrar)
    const renderProgressGroup = () => (
      <div className="ui-flex ui-shrink-0 ui-items-center ui-gap-4">
        {/* Progress info group */}
        {showProgress && (
          <span
            className={cn(
              'ui-flex ui-items-center',
              'ui-text-body-xs-regular ui-font-body-xs-medium',
              'ui-whitespace-nowrap ui-text-secondary',
            )}
          >
            <span className="ui-flex ui-items-center">
              <span className="ui-text-right">{Math.round(effectiveProgress)}</span>
              <span>{progressSuffix}</span>
            </span>
            {eta && <span className="ui-ml-1">({eta})</span>}
          </span>
        )}

        {/* Close button */}
        {showCloseButton && (
          <Button
            theme="solid"
            color="tertiary"
            size="xxs"
            pill="pill"
            icon={faXmark}
            onClick={handleClose}
            className="ui-rounded-control-pill ui-p-2"
            aria-label="Cerrar"
          />
        )}
      </div>
    );

    return (
      <div className={containerClasses}>
        {/* ===== MODO COMPACTO: Todo en una línea ===== */}
        {compact ? (
          <div className="ui-flex ui-w-full ui-items-center ui-gap-8">
            {/* Label */}
            {showLabel && label && <LabelProgress label={label} className="ui-shrink-0" />}

            {/* Barra de progreso - flex-1 para ocupar espacio disponible */}
            <BarProgress
              progress={effectiveProgress}
              size={size}
              color={color}
              duration={effectiveDuration}
              withBackground={withBackground}
              className="ui-min-w-0 ui-flex-1"
            />

            {/* Progress group */}
            {renderProgressGroup()}
          </div>
        ) : (
          /* ===== MODO NORMAL: Header arriba, barra abajo ===== */
          <>
            {/* Header */}
            <div className="ui-flex ui-w-full ui-items-center ui-justify-between">
              {/* Label */}
              {showLabel && label && <LabelProgress label={label} />}

              {/* Spacer */}
              <div className="ui-min-h-px ui-flex-1" />

              {/* Progress group */}
              {renderProgressGroup()}
            </div>

            {/* Barra de progreso */}
            <BarProgress
              progress={effectiveProgress}
              size={size}
              color={color}
              duration={effectiveDuration}
              withBackground={withBackground}
            />
          </>
        )}

        {/* Helper text - siempre debajo */}
        {helperText && <HelperText type={helperTextType}>{helperText}</HelperText>}
      </div>
    );
  },
);

ProgressBar.displayName = 'ProgressBar';
